import { IAPIRoutes } from "../../../../config";

export type ICompassusNavigatorRepository = ReturnType<
  typeof CompassusNavigatorRepositoryFactory
>;

export const CompassusNavigatorRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { DELETE, GET, PATCH, POST, PUT } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const buildQueryParams = (
    params?: Record<string, unknown>
  ): Record<string, string | number> => {
    if (!params) return {};
    const cleaned: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      // Arrays are handled separately via appendArrayParams — skip here
      if (Array.isArray(value)) continue;
      cleaned[key] = value as string | number;
    }
    return cleaned;
  };

  /**
   * Appends repeated query params for array values, e.g.
   * ["CIGNA", "Medicaid"] → ?payer_names=CIGNA&payer_names=Medicaid
   */
  const appendArrayParams = (
    baseUrl: string,
    params?: Record<string, unknown>
  ): string => {
    if (!params) return baseUrl;
    const parts: string[] = [];
    for (const [key, value] of Object.entries(params)) {
      if (!Array.isArray(value) || value.length === 0) continue;
      for (const item of value) {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
        );
      }
    }
    if (parts.length === 0) return baseUrl;
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${parts.join("&")}`;
  };

  const getPatientsAPI: IGetCompassusPatientsAPI = async (payload) => {
    const baseUrl = APIRoutes.apiCompassusNavigator.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    const params = payload.params as Record<string, unknown> | undefined;
    const url = appendArrayParams(baseUrl, params);

    return GET(url, buildQueryParams(params), {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  /**
   * Same endpoint as getPatientsAPI, but forces `ids_only=true`. Backend
   * skips the expensive joins (conditions/payers/meds/allergies/visits) and
   * returns a flat `{ patient_ids, total_count }` payload with no
   * page_size cap. Used for "select all patients matching current filters".
   */
  const getPatientIdsAPI: IGetCompassusPatientIdsAPI = async (payload) => {
    const baseUrl = APIRoutes.apiCompassusNavigator.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    const params = {
      ...(payload.params ?? {}),
      ids_only: true,
    } as Record<string, unknown>;
    const url = appendArrayParams(baseUrl, params);

    return GET(url, buildQueryParams(params), {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const exportNavigatorAPI: IExportCompassusNavigatorAPI = async (payload) => {
    const url = APIRoutes.apiCompassusNavigatorExport.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );

    return POST(
      url,
      payload.body,
      { Authorization: `Bearer ${await getAuthenticationToken()}` },
      { responseType: "blob" }
    );
  };

  const getCountsAPI: IGetCompassusCountsAPI = async (payload) => {
    const baseUrl = APIRoutes.apiCompassusNavigatorCounts.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    const params = payload.params as Record<string, unknown> | undefined;
    const url = appendArrayParams(baseUrl, params);

    return GET(url, buildQueryParams(params), {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPayerFiltersAPI: IGetCompassusPayerFiltersAPI = async (payload) => {
    const url = APIRoutes.apiCompassusNavigatorFilterPayers.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getMedicationFiltersAPI: IGetCompassusStringFiltersAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorFilterMedications.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getAllergyFiltersAPI: IGetCompassusStringFiltersAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorFilterAllergies.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getConditionFiltersAPI: IGetCompassusStringFiltersAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorFilterConditions.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getLastVisitProviderFiltersAPI: IGetCompassusProviderFiltersAPI =
    async (payload) => {
      const url =
        APIRoutes.apiCompassusNavigatorFilterLastVisitProviders.replace(
          "{clinic_id}",
          payload.clinic_id.toString()
        );
      return GET(url, buildQueryParams(payload.params), {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const getNextVisitProviderFiltersAPI: IGetCompassusProviderFiltersAPI =
    async (payload) => {
      const url =
        APIRoutes.apiCompassusNavigatorFilterNextVisitProviders.replace(
          "{clinic_id}",
          payload.clinic_id.toString()
        );
      return GET(url, buildQueryParams(payload.params), {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const getTodoTagFiltersAPI: IGetCompassusStringFiltersAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorFilterTodoTags.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getCustomTagFiltersAPI: IGetCompassusStringFiltersAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorFilterCustomTags.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getClinicalRiskScoreFiltersAPI: IGetCompassusClinicalRiskScoreFiltersAPI =
    async (payload) => {
      const url =
        APIRoutes.apiCompassusNavigatorFilterClinicalRiskScores.replace(
          "{clinic_id}",
          payload.clinic_id.toString()
        );
      return GET(
        url,
        {},
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );
    };

  const patchPatientTodoAPI: IPatchCompassusPatientTodoAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorPatientTodo
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return PATCH(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPatientHhJourneyStatusAPI: IGetCompassusPatientHhJourneyStatusAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusNavigatorPatientHhJourneyStatus
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const patchPatientHhJourneyStatusAPI: IPatchCompassusPatientHhJourneyStatusAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusNavigatorPatientHhJourneyStatus
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return PATCH(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const getPatientDataReviewAPI: IGetCompassusPatientDataReviewAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorPatientDataReview
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getPatientInsuranceAPI: IGetCompassusPatientInsuranceAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorPatientInsurance
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getPatientInsuranceRawResponseAPI: IGetCompassusPatientInsuranceRawResponseAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusNavigatorPatientInsuranceResponse
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getHhEligibilityWorklistAPI: IGetCompassusHhEligibilityWorklistAPI =
    async (payload) => {
      const baseUrl =
        APIRoutes.apiCompassusNavigatorHhEligibilityWorklist.replace(
          "{clinic_id}",
          payload.clinic_id.toString()
        );
      const params: Record<string, unknown> = { ...payload.params };
      const url = appendArrayParams(baseUrl, params);
      return GET(url, buildQueryParams(params), {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const getHhNpWorklistAPI: IGetCompassusHhNpWorklistAPI = async (payload) => {
    const baseUrl = APIRoutes.apiCompassusNavigatorHhNpWorklist.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    const params = payload.params as Record<string, unknown>;
    const url = appendArrayParams(baseUrl, params);
    return GET(url, buildQueryParams(params), {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getHhTechnicianWorklistAPI: IGetCompassusHhTechnicianWorklistAPI =
    async (payload) => {
      const baseUrl =
        APIRoutes.apiCompassusNavigatorHhTechnicianWorklist.replace(
          "{clinic_id}",
          payload.clinic_id.toString()
        );
      const params = payload.params as Record<string, unknown>;
      const url = appendArrayParams(baseUrl, params);
      return GET(url, buildQueryParams(params), {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const patchHhNpUnlinkDraftAPI: IPatchCompassusHhNpUnlinkDraftAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorHhNpUnlinkDraft
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{assignment_id}", payload.assignment_id.toString());
    return PATCH(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const patchHhVisitScheduleAPI: IPatchCompassusHhVisitScheduleAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorHhVisitSchedule
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{assignment_id}", payload.assignment_id.toString());
    return PATCH(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const postHhAtRiskFollowUpAPI: IPostCompassusHhAtRiskFollowUpAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorPatientHhAtRiskFollowUp
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return POST(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const postPatientCustomTagsAPI: IPostCompassusPatientCustomTagsAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusPatientCustomTags
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return POST(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPatientCustomTagsAPI: IGetCompassusPatientCustomTagsAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusPatientCustomTags
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getPatientCommunicationNumbersAPI: IGetCompassusPatientCommunicationNumbersAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationNumbers
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const postPatientCommunicationNumberAPI: IPostCompassusPatientCommunicationNumberAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationNumbers
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return POST(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const putPatientCommunicationPrimaryNumberAPI: IPutCompassusPatientCommunicationPrimaryNumberAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationPrimaryNumber
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return PUT(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const patchPatientCommunicationNumberAPI: IPatchCompassusPatientCommunicationNumberAPI =
    async (payload) => {
      const url = `${APIRoutes.apiCompassusPatientCommunicationNumbers
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString())}/${
        payload.link_id
      }`;
      return PATCH(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const deletePatientCommunicationNumberAPI: IDeleteCompassusPatientCommunicationNumberAPI =
    async (payload) => {
      const url = `${APIRoutes.apiCompassusPatientCommunicationNumbers
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString())}/${
        payload.link_id
      }`;
      return DELETE(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getPatientCommunicationSmsAPI: IGetCompassusPatientCommunicationSmsAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationSms
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        buildQueryParams({
          phone_number: payload.phone_number,
          limit: payload.limit,
        }),
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getPatientCommunicationSmsThreadsAPI: IGetCompassusPatientCommunicationSmsThreadsAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationSmsThreads
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const postPatientCommunicationSmsAPI: IPostCompassusPatientCommunicationSmsAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationSms
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return POST(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const postPatientNpAppointmentSmsAPI: IPostCompassusPatientNpAppointmentSmsAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientNpAppointmentSms
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return POST(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const getPatientCommunicationCallsAPI: IGetCompassusPatientCommunicationCallsAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationCalls
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(url, buildQueryParams({ limit: payload.limit }), {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const postPatientCommunicationCallTokenAPI: IPostCompassusPatientCommunicationCallTokenAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationCallToken
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return POST(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const postPatientCommunicationCallAPI: IPostCompassusPatientCommunicationCallAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusPatientCommunicationCalls
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return POST(url, payload.body, {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      });
    };

  const getPatientAssociationsAPI: IGetCompassusPatientAssociationsAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusPatientAssociations
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  /**
   * Bulk to-do mutation. One request covers every patient in `patient_ids`
   * with a single `mode`. See port docs for mode semantics.
   */
  const bulkPatchPatientTodoAPI: IBulkPatchCompassusPatientTodoAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorPatientTodoBulk.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return PATCH(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const putWatchlistAPI: IPutCompassusWatchlistAPI = async (payload) => {
    const url = APIRoutes.apiPutWatchlist.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return PUT(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const patchWatchlistAPI: IPatchCompassusWatchlistAPI = async (payload) => {
    const url = APIRoutes.apiPatchWatchlist.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return PATCH(url, payload.body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getAcuityLevelFiltersAPI: IGetCompassusStringFiltersAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorFilterAcuityLevels.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getPatientAcuityLevelHistoryAPI: IGetCompassusPatientAcuityLevelHistoryAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusNavigatorPatientAcuityLevels
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getPatientPrognosisHistoryAPI: IGetCompassusPatientPrognosisHistoryAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusNavigatorPatientPrognoses
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getPatientFormHistoryAPI: IGetCompassusPatientFormHistoryAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCompassusNavigatorPatientForms
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getPatientHospiceEligibilityScoreAPI: IGetCompassusPatientHospiceEligibilityScoreAPI =
    async (payload) => {
      const url = APIRoutes.apiCompassusNavigatorPatientHospiceEligibilityScore
        .replace("{clinic_id}", payload.clinic_id.toString())
        .replace("{patient_id}", payload.patient_id.toString());
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getPatientHospiceEligibilityScoreHistoryAPI: IGetCompassusPatientHospiceEligibilityScoreHistoryAPI =
    async (payload) => {
      const url =
        APIRoutes.apiCompassusNavigatorPatientHospiceEligibilityScoreHistory.replace(
          "{patient_id}",
          payload.patient_id.toString()
        );
      return GET(
        url,
        buildQueryParams({
          include_rubric: payload.include_rubric,
          limit: payload.limit,
        }),
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const getPatientHospiceEligibilityScoreLlmRefreshStatusAPI: IGetCompassusPatientHospiceEligibilityScoreLlmRefreshStatusAPI =
    async (payload) => {
      const url =
        APIRoutes.apiCompassusNavigatorPatientHospiceEligibilityScoreLlmRefreshStatus.replace(
          "{patient_id}",
          payload.patient_id.toString()
        ) + "?status_for=llm-refresh";
      return GET(
        url,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const postPatientHospiceEligibilityScoreLlmRefreshAPI: IPostCompassusPatientHospiceEligibilityScoreLlmRefreshAPI =
    async (payload) => {
      const url =
        APIRoutes.apiCompassusNavigatorPatientHospiceEligibilityScoreLlmRefresh.replace(
          "{patient_id}",
          payload.patient_id.toString()
        );
      return POST(
        url,
        {},
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );
    };

  const getFormsSchemaAPI: IGetCompassusFormsSchemaAPI = async (payload) => {
    const url = APIRoutes.apiCompassusNavigatorFormsSchema.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  return {
    getPatientsAPI,
    getPatientIdsAPI,
    exportNavigatorAPI,
    getCountsAPI,
    getPayerFiltersAPI,
    getMedicationFiltersAPI,
    getAllergyFiltersAPI,
    getConditionFiltersAPI,
    getLastVisitProviderFiltersAPI,
    getNextVisitProviderFiltersAPI,
    getTodoTagFiltersAPI,
    getCustomTagFiltersAPI,
    getClinicalRiskScoreFiltersAPI,
    patchPatientTodoAPI,
    getPatientHhJourneyStatusAPI,
    patchPatientHhJourneyStatusAPI,
    getPatientDataReviewAPI,
    getPatientInsuranceAPI,
    getPatientInsuranceRawResponseAPI,
    getHhEligibilityWorklistAPI,
    getHhNpWorklistAPI,
    getHhTechnicianWorklistAPI,
    patchHhNpUnlinkDraftAPI,
    patchHhVisitScheduleAPI,
    postHhAtRiskFollowUpAPI,
    postPatientCustomTagsAPI,
    getPatientCustomTagsAPI,
    getPatientCommunicationNumbersAPI,
    postPatientCommunicationNumberAPI,
    putPatientCommunicationPrimaryNumberAPI,
    patchPatientCommunicationNumberAPI,
    deletePatientCommunicationNumberAPI,
    getPatientCommunicationSmsAPI,
    getPatientCommunicationSmsThreadsAPI,
    postPatientCommunicationSmsAPI,
    postPatientNpAppointmentSmsAPI,
    getPatientCommunicationCallsAPI,
    postPatientCommunicationCallTokenAPI,
    postPatientCommunicationCallAPI,
    getPatientAssociationsAPI,
    bulkPatchPatientTodoAPI,
    putWatchlistAPI,
    patchWatchlistAPI,
    getAcuityLevelFiltersAPI,
    getPatientAcuityLevelHistoryAPI,
    getPatientPrognosisHistoryAPI,
    getPatientFormHistoryAPI,
    getPatientHospiceEligibilityScoreAPI,
    getPatientHospiceEligibilityScoreHistoryAPI,
    getPatientHospiceEligibilityScoreLlmRefreshStatusAPI,
    postPatientHospiceEligibilityScoreLlmRefreshAPI,
    getFormsSchemaAPI,
  };
};
